<template>
    <config-base showPlanDetails class="opc-webinar" ref="baseRef" :on="$listeners">
      <template #topAlert>
        <zm-alert v-if="hasOneTimeWebinar" type="warning" show-icon :closable="false" class="opc-base-alert">
          <p class="opc-base-alert__message" v-html="notification"  @mouseover="() => $tracking.buyInteraction('banner - product level', trackingPlanKey + ' - ' + notification + ' - impression')" />
        </zm-alert>
      </template>
      <template #config>
        <capacity-config-list :config-plan-type="planType" :config-capacity-key="capacityKey" :configRowName='configRowName' capacityUnitI18nKey='billing.attendees'></capacity-config-list>
      </template>
    </config-base>
  </template>

  <script>
  import _, { get, debounce } from "lodash";
  import {mapGetters} from "vuex";
  import planCommon from '@/plans/WEBINAR/mixin';
  import {getPlanTotalLicense} from "@/common/service/validate";
  import {getNeedFilterCapacityList} from '@/common/product';
  import {computedWebinarPlans} from "@/common/service/computers/plans/webinar";
  import plansMixin from "@/plans/mx/plan";
  import ConfigList from "@/components/capacity/config-list.vue";
  import ConfigBase from "@/plans/WEBINAR/config/base.vue";
  import autoAddMixin from "@/components/capacity/mx/autoAdd";
  import {
    MAX_SINGLE_USE_WEBINAR_LICENSE_COUNT,
    WEBINAR_ONE_TIME_CAPACITY,
    planTypes, BILLING_CYCLE,
  } from '@/common/constants';
  import MixedPlans from '../mixins/mixed-plans'
  import TwoWayControl from '@/plans/WEBINAR/mixins/two-way-control';

  const CapacityConfigList = {
    name: 'CapacityConfigList',
    extends: ConfigList,
    mixins: [planCommon, autoAddMixin, MixedPlans, TwoWayControl],
    data() {
      return {
        // license number of current selected capacity
        currentCapacityLicense: new Map(),
      };
    },
    computed: {
      ...mapGetters(['getCurrentMeetingPlans', 'getCurrentBasePlanInfo', 'boughtPlans', 'hasBoughtDependPlan', 'basicConfig', 'currentEditPlan']),
      addAnotherCapacityCta() {
        return this.$t('billing.addAnother.webinar');
      },
      boughtCurrentPlans() {
        return computedWebinarPlans(this.boughtPlans)
      },
      planList() {
        const beforeList = _.filter(this.planMapFlat, p => p.type === this.planType).filter((p) => {
          const isSupportCapacity = this.supportCapacity.includes(p[this.capacityKey]) || WEBINAR_ONE_TIME_CAPACITY.includes(p.capacity);
          // Only filter the paid bought capacity, the 500 ft capacity is needed here for upgrade
          const isBoughtCapacity = this.boughtCurrentPlans.filter(k => !k.isOnlineFreeTrial && !k.isFreeTrial).map(k=>
            k.isFreeTrial? k.capacity: k[this.capacityKey]
          ).includes(p[this.capacityKey])

          const needFilterCapacity = getNeedFilterCapacityList(this.getCurrentBasePlanInfo?.plans).includes(p[this.capacityKey])
          return isSupportCapacity && !needFilterCapacity && !isBoughtCapacity;
        });

        const afterList = _.filter(beforeList, (p) => {
          const currentCapacity = this.currentEditPlan?.[this.capacityKey];
          const isBulkLicenseAccount = this.basicConfig?.isBulkLicenseAccount;

          if (isBulkLicenseAccount) {
            return currentCapacity === p?.[this.capacityKey];
          }

          return true;
        });

        return !afterList?.length ? beforeList : afterList;
      },
      mx_showSelectCapacityDropdown() {
        return this.allCapacityOrderList.length !== 1;
      },
      currentBasePlans() {
        return this.getCurrentMeetingPlans.length ? this.getCurrentMeetingPlans : this.getCurrentBasePlanInfo?.plans;
      },
      editTotalLimitDesc() {
        const basePlanNameKey = get(this.currentBasePlans, [0, 'parentPlanNameKey']) || get(this.currentBasePlans, [0, 'planNameKey']) || get(this.currentBasePlans, [0, 'typeNameKey'])
        return this.$t(
          this.hasBoughtDependPlan ?
            'billing.addon_license_limit_tips' :
            'billing.addon_license_limit_tips_without_contact',
          [this.title || this.$t('billing.webinar.title'), this.$t(basePlanNameKey)])
      }
    },

    methods: {
      handleLicenseChange(license, oldLicense, planInfo, key) {
        let handler
        if (key) {
          if (!this.$_handleLicenseChangeMap) {
            this.$_handleLicenseChangeMap = Object.create(null)
          }
          if (!this.$_handleLicenseChangeMap[key]) {
            // Debounce? From the legacy logic
            this.$_handleLicenseChangeMap[key] = debounce(this.handleLicenseChangeFn.bind(this), 300)
          }
          handler = this.$_handleLicenseChangeMap[key]
        } else {
          if (!this.$_handleLicenseChangeDebounce) {
            // Debounce? From the legacy logic
            this.$_handleLicenseChangeDebounce = debounce(this.handleLicenseChangeFn.bind(this), 300)
          }
          handler = this.$_handleLicenseChangeDebounce
        }
        handler(license, oldLicense, planInfo)
      },
      handleLicenseChangeFn(license, oldLicense, planInfo) {
        const { capacity } = planInfo;
        const maxHosts = this.mx_getMaxHosts(capacity)
        const condition = license >= maxHosts && maxHosts !== 999
        this.$set(this.mx_license_capacityAlertMap, capacity, condition ? this.editTotalLimitDesc : "");
        if (this.currentCapacityLicense.has(capacity)) {
          this.currentCapacityLicense.set(capacity, license);
          this.syncAutoAddBasePlanLicense(planInfo);
        }
      },

      /**
       * sync license change to base plan of auto add
       * @param {object} planInfo
       * @param {number} planInfo.capacity
       * @param {string} planInfo.billingCycle
       */
      syncAutoAddBasePlanLicense({ capacity, billingCycle }) {
        if (this.mx_canAutoAdd && billingCycle !== BILLING_CYCLE.ONE_TIME) {
          const maxLicense = this.getMaxCapacityLicense();
          // cancel switch plan, reset license
          this.mx_handlePlanLicenseChange(maxLicense).catch((previousLicense) => {
            const configRowInstance = get(this.$refs, [`configRow${capacity}`, '0']);
            if (configRowInstance) {
              configRowInstance.changeLicense(previousLicense);
              this.currentCapacityLicense.set(capacity, previousLicense);
            }
          });
        }
      },
      mx_getMaxHosts(capacity) {
        if (this.mx_isCapacityBelongToOneTime(capacity)) return MAX_SINGLE_USE_WEBINAR_LICENSE_COUNT
        if (this.mx_canAutoAdd) {
          return this.mx_maxAutoAddHosts;
        }
        return getPlanTotalLicense(this.currentBasePlans || []) || 999;
      },
      mx_getDeleteVisible(capacity){
        return !(
          this.selectedPlanList?.length === 1
          && capacity === this.selectedPlanList?.[0].capacity)
      },

      /**
       * get maximum number of current webinar license that allow auto add
       */
      getMaxCapacityLicense() {
        let max = 0;
        this.currentCapacityLicense.forEach((license, capacity) => {
          if (!this.mx_isCapacityBelongToOneTime(capacity) && license > max) {
            max = license;
          }
        })
        return max;
      },
      /**
       * after add new capacity, update license Map
       */
      afterAddCapacityHook(capacity) {
        this.currentCapacityLicense.set(capacity, 1);
      },
      /**
       * after delete capacity, update license Map
       * @param {object} planInfo deleted plan
       * @param {number} planInfo.capacity deleted plan capacity
       * @param {string} planInfo.billingCycle deleted plan billing cycle
       * @param {number} planInfo.license deleted plan license number
       */
      afterDeleteCapacityHook(planInfo) {
        this.currentCapacityLicense.delete(planInfo.capacity);
        this.syncAutoAddBasePlanLicense(planInfo);
      },
      /**
       * after switch capacity, update license Map
       * 1. delete previous one
       * 2. add new one
       */
      afterSwitchCapacityHook(oldPlan, newPlan) {
        const newPlanIsOneTime = newPlan.billing_cycle === BILLING_CYCLE.ONE_TIME;
        const oldPlanIsOneTime = oldPlan.billing_cycle === BILLING_CYCLE.ONE_TIME;
        const planInfo = newPlanIsOneTime !== oldPlanIsOneTime && newPlanIsOneTime ? oldPlan : newPlan;
        const oldCapacityLicense = this.currentCapacityLicense.get(oldPlan.capacity);
        this.currentCapacityLicense.delete(oldPlan.capacity);
        this.currentCapacityLicense.set(newPlan.capacity, oldCapacityLicense);
        this.syncAutoAddBasePlanLicense({ capacity: planInfo.capacity, billingCycle: planInfo.billing_cycle });
      },
    },
    mounted() {
      // init number of config row license
      this.visibleCapacityList.forEach((capacity) => {
        const license = get(
          this.mx_getPlanInfoByCapacity(capacity),
          'cartInfo.selected_number',
        );
        if (license) {
          this.currentCapacityLicense.set(capacity, license);
        }
      });
    },
  }
  export default {
    mixins: [plansMixin, planCommon],
    components: { ConfigBase, CapacityConfigList },
    computed: {
      configRowName() {
        return 'CapacityConfigRowV2';
      },
      hasOneTimeWebinar() {
        return this.getSelectedPlans.find(p => p.type === planTypes.WEBINAR && p.billing_cycle === BILLING_CYCLE.ONE_TIME);
      },
      notification() {
        return this.$t('billing.single_use_webinar.notification')
      },
    }
  };
  </script>

  <style lang='scss'>
  </style>
